import React, { useCallback, useEffect, useRef, useState } from 'react'
import useDeepCompareEffect from 'use-deep-compare-effect'

import dataApi from 'utilities/data'
import { CarFileMarktplaatsStatusEnum } from 'config/enums'

/**
 * Manages car selection state in the stock view.
 *
 * Handles selecting/deselecting individual cars, select-all (including
 * fetching beyond the current page), and re-validating the selection
 * when filters change so that only cars still in the filtered results
 * remain selected.
 */
const useStockSelection = ({
  stockResults,
  queryFilters,
  sortOption,
  instanceId,
  selectedViewType,
}) => {
  const [selectedCarIds, setSelectedCarIds] = useState([])
  const [allFetchedItems, setAllFetchedItems] = useState(null)
  const [isLoadingAllCars, setIsLoadingAllCars] = useState(false)

  // Reset selection when the view type changes (e.g. marktplaats ↔ advertisement)
  useEffect(() => {
    setSelectedCarIds([])
    setAllFetchedItems(null)
  }, [selectedViewType])

  // When stock results update (e.g. page change) and we don't have a full
  // fetch, trim the selection to only include visible items.
  useEffect(() => {
    if (stockResults?.loading || !stockResults?.data?.items) {
      return
    }
    if (allFetchedItems) {
      return
    }

    setSelectedCarIds((prev) => {
      if (!prev.length) {
        return prev
      }
      const total = stockResults.data.total
      const currentPageItems = stockResults.data.items
      // When all results fit on one page, filter against visible items
      if (total <= currentPageItems.length) {
        const visibleIds = new Set(
          currentPageItems.map((item) => String(item.id)),
        )
        const filtered = prev.filter((id) => visibleIds.has(id))
        return filtered.length === prev.length ? prev : filtered
      }
      // Multi-page: handled by the filter-change effect below
      return prev
    })
  }, [stockResults, allFetchedItems])

  // When filters change, re-validate the selection by fetching all matching
  // items and intersecting with the current selection.
  const filtersInitializedRef = useRef(false)
  const selectedCarIdsRef = useRef(selectedCarIds)
  selectedCarIdsRef.current = selectedCarIds

  useDeepCompareEffect(() => {
    if (!filtersInitializedRef.current) {
      filtersInitializedRef.current = true
      return
    }

    setAllFetchedItems(null)

    if (!selectedCarIdsRef.current.length) {
      return
    }

    let cancelled = false
    const revalidateSelection = async () => {
      setIsLoadingAllCars(true)
      try {
        const allResults = await dataApi.getStockResults(instanceId, {
          ...queryFilters,
          dir: sortOption?.dir,
          sort: sortOption?.field,
          page: 1,
          lines: 10000,
        })
        if (cancelled) {
          return
        }
        const allIds = new Set(allResults.items.map((item) => String(item.id)))
        setSelectedCarIds((prev) => prev.filter((id) => allIds.has(id)))
        setAllFetchedItems(allResults.items)
      } catch (error) {
        if (cancelled) {
          return
        }
        console.error('Failed to re-validate selection:', error)
        setSelectedCarIds([])
      }
      setIsLoadingAllCars(false)
    }
    revalidateSelection()

    return () => {
      cancelled = true
    }
  }, [queryFilters])

  const handleSelectCar = useCallback((carId, checked) => {
    setSelectedCarIds((prev) => {
      if (checked) {
        return [...prev, carId]
      }
      return prev.filter((id) => id !== carId)
    })
  }, [])

  const handleSelectAll = useCallback(async () => {
    if (!stockResults?.data?.items) {
      return
    }

    if (selectedCarIds.length) {
      setSelectedCarIds([])
      setAllFetchedItems(null)
    } else {
      const total = stockResults.data.total
      const currentPageItems = stockResults.data.items

      if (total > currentPageItems.length) {
        setIsLoadingAllCars(true)
        try {
          const allResults = await dataApi.getStockResults(instanceId, {
            ...queryFilters,
            dir: sortOption.dir,
            sort: sortOption.field,
            page: 1,
            lines: total,
          })
          setAllFetchedItems(allResults.items)
          setSelectedCarIds(allResults.items.map((item) => String(item.id)))
        } catch (error) {
          console.error('Failed to fetch all car IDs:', error)
          setSelectedCarIds(
            currentPageItems.map((item) => String(item.id)),
          )
        }
        setIsLoadingAllCars(false)
      } else {
        setSelectedCarIds(currentPageItems.map((item) => String(item.id)))
      }
    }
  }, [stockResults, selectedCarIds.length, queryFilters, sortOption, instanceId])

  const unpublishedCarIds = React.useMemo(() => {
    const items = allFetchedItems || stockResults?.data?.items || []
    return items
      .filter(
        (car) =>
          selectedCarIds.includes(car.id.toString()) &&
          (car.marktplaats?.status ===
            CarFileMarktplaatsStatusEnum.NOT_PUBLISHED ||
            car.marktplaats?.status ===
              CarFileMarktplaatsStatusEnum.UNPUBLISHED),
      )
      .map((car) => car.id.toString())
  }, [selectedCarIds, stockResults, allFetchedItems])

  return {
    selectedCarIds,
    allFetchedItems,
    isLoadingAllCars,
    unpublishedCarIds,
    handleSelectCar,
    handleSelectAll,
  }
}

export default useStockSelection
